import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Rocket, Trash2, Settings, Database, BookOpen, MessageSquare, Globe } from 'lucide-react';
import { LoadingSpinner } from '../../../../common';
import { AdminPageHeader, AdminTabs, AdminEmptyState } from '../../../ui';
import * as agentService from '../../../../../services/agentService';
import { Agent } from '../../../../../types/agent';
import { AgentConfiguration } from './AgentConfiguration';
import { AgentKnowledgeBase } from './AgentKnowledgeBase';
import { AgentDataSource } from './AgentDataSource';
import { AgentTest } from './AgentTest';
import { PublishGlobalModal } from './PublishGlobalModal';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useWorkspace } from '../../../../../contexts/WorkspaceContext';

type TabId = 'config' | 'knowledge' | 'datasource' | 'test';

const tabs = [
  { id: 'config' as TabId, label: 'Configuração', icon: <Settings className="w-4 h-4" /> },
  { id: 'knowledge' as TabId, label: 'Base de Conhecimento', icon: <BookOpen className="w-4 h-4" /> },
  { id: 'datasource' as TabId, label: 'Data Sources', icon: <Database className="w-4 h-4" /> },
  { id: 'test' as TabId, label: 'Testar Agente', icon: <MessageSquare className="w-4 h-4" /> },
];

export const AgentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('config');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTogglingDeployment, setIsTogglingDeployment] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  useEffect(() => {
    if (id) {
      fetchAgentDetails(parseInt(id));
    }
  }, [id]);

  const fetchAgentDetails = async (agentId: number) => {
    try {
      setError(null);
      const response = await agentService.getAgent(agentId);
      if (response.success && response.data) {
        setAgent(response.data);
      } else {
        setError(response.message || 'Falha ao carregar detalhes do agente');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar detalhes do agente');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleDeployment = async () => {
    if (!agent) return;

    try {
      setIsTogglingDeployment(true);
      setError(null);
      const response = await agentService.toggleAgentDeployment(agent.id);

      if (response.success && response.data) {
        setAgent(response.data);
      } else {
        setError(response.message || 'Falha ao alterar status de implantação');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao alterar status de implantação');
    } finally {
      setIsTogglingDeployment(false);
    }
  };

  const handleDeleteAgent = async () => {
    if (!agent) return;

    try {
      setIsDeleting(true);
      setError(null);
      const response = await agentService.deleteAgent(agent.id);

      if (response.success) {
        navigate('/admin/agents', { replace: true });
      } else {
        setError(response.message || 'Falha ao excluir agente');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao excluir agente');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handlePublishGlobal = async () => {
    if (!agent) return;

    try {
      setIsPublishing(true);
      setError(null);
      const updatedAgent = await agentService.publishAgentGlobal(agent.id);
      setAgent(updatedAgent);
      setShowPublishModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao publicar agente globalmente');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!agent) return;

    try {
      setIsPublishing(true);
      setError(null);
      const updatedAgent = await agentService.unpublishAgent(agent.id);
      setAgent(updatedAgent);
      setShowUnpublishModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao despublicar agente');
    } finally {
      setIsPublishing(false);
    }
  };

  // Check if user can publish globally (TD workspace + permission)
  const canPublishGlobal = useMemo(() => {
    if (!currentWorkspace?.settings?.can_publish_global_agents) return false;
    return user?.canManageAgents || user?.canAdmin || false;
  }, [currentWorkspace, user]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
        <AdminEmptyState
          title="Agente não encontrado"
          description="O agente solicitado não existe ou foi removido"
          action={
            <button
              onClick={() => navigate('/admin/agents')}
              className="
                flex items-center gap-2 px-4 py-2
                bg-[#F4CC04] hover:bg-[#D4B104]
                text-black font-medium
                rounded-lg transition-colors
              "
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para Agentes
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
      {/* Header */}
      <AdminPageHeader
        title={agent.name}
        subtitle={agent.description || 'Sem descrição'}
        backPath="/admin/agents"
        actions={
          <div className="flex items-center gap-3">
            {/* Visibility Badge */}
            {agent.visibility_level === 'global' && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600/20 text-purple-300 text-sm font-medium rounded-full">
                <Globe className="w-3.5 h-3.5" />
                Global
              </span>
            )}

            {/* Status Badge */}
            <span
              className={`
                px-3 py-1.5 text-sm font-medium rounded-full
                ${agent.deploy_status
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-[#333333] text-[#888888]'
                }
              `}
            >
              {agent.deploy_status ? 'Deployed' : 'Draft'}
            </span>

            {/* Deploy/Undeploy Button */}
            <button
              onClick={handleToggleDeployment}
              disabled={isTogglingDeployment}
              className={`
                flex items-center gap-2 px-4 py-2
                text-sm font-medium rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
                ${agent.deploy_status
                  ? 'bg-[#333333] hover:bg-[#444444] text-white'
                  : 'bg-[#F4CC04] hover:bg-[#D4B104] text-black'
                }
              `}
            >
              <Rocket className="w-4 h-4" />
              {isTogglingDeployment
                ? (agent.deploy_status ? 'Removendo...' : 'Implantando...')
                : (agent.deploy_status ? 'Remover Deploy' : 'Deploy')
              }
            </button>

            {/* Publish/Unpublish Global Button (TD workspace only) */}
            {canPublishGlobal && agent.visibility_level === 'workspace' && (
              <button
                onClick={() => setShowPublishModal(true)}
                disabled={isPublishing}
                className="
                  flex items-center gap-2 px-4 py-2
                  bg-purple-600 hover:bg-purple-700
                  text-white text-sm font-medium
                  rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                <Globe className="w-4 h-4" />
                Publicar Globalmente
              </button>
            )}

            {canPublishGlobal && agent.visibility_level === 'global' && (
              <button
                onClick={() => setShowUnpublishModal(true)}
                disabled={isPublishing}
                className="
                  flex items-center gap-2 px-4 py-2
                  bg-[#333333] hover:bg-[#444444]
                  text-white text-sm font-medium
                  rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                <Globe className="w-4 h-4" />
                Despublicar
              </button>
            )}

            {/* Delete Button */}
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={isDeleting}
              className="
                flex items-center gap-2 px-4 py-2
                bg-red-500/10 hover:bg-red-500/20
                text-red-400 text-sm font-medium
                rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              <Trash2 className="w-4 h-4" />
              Excluir
            </button>
          </div>
        }
      />

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm mb-6">
          {error}
        </div>
      )}

      {/* Publication Info (for global agents) */}
      {agent.visibility_level === 'global' && agent.published_at && (
        <div className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-[#999999] mb-2">
            Informações de Publicação Global
          </h3>
          <div className="space-y-1 text-sm">
            <p className="text-white">
              <span className="text-[#999999]">Publicado em:</span>{' '}
              {new Date(agent.published_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
            {agent.published_by && (
              <p className="text-white">
                <span className="text-[#999999]">Por:</span> Usuário ID {agent.published_by}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <AdminTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as TabId)}
        />
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'config' && (
          <AgentConfiguration
            agent={agent}
            onUpdate={() => fetchAgentDetails(parseInt(id!))}
          />
        )}

        {activeTab === 'knowledge' && (
          <AgentKnowledgeBase
            agent={agent}
            onUpdate={() => fetchAgentDetails(parseInt(id!))}
          />
        )}

        {activeTab === 'datasource' && (
          <AgentDataSource
            agent={agent}
            onUpdate={() => fetchAgentDetails(parseInt(id!))}
          />
        )}

        {activeTab === 'test' && <AgentTest />}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40"
            onClick={() => !isDeleting && setShowDeleteModal(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl p-6 z-50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Excluir Agente</h2>
              {!isDeleting && (
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
                >
                  <span className="text-xl">&times;</span>
                </button>
              )}
            </div>

            <p className="text-[#cccccc] mb-6">
              Tem certeza que deseja excluir o agente "<span className="text-white font-medium">{agent.name}</span>"? Esta ação não pode ser desfeita.
            </p>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="
                  px-4 py-2
                  bg-[#333333] hover:bg-[#444444]
                  text-white text-sm font-medium
                  rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteAgent}
                disabled={isDeleting}
                className="
                  flex items-center gap-2 px-4 py-2
                  bg-red-500 hover:bg-red-600
                  text-white text-sm font-medium
                  rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Excluir Agente
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Publish Global Modal */}
      <PublishGlobalModal
        agent={agent}
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handlePublishGlobal}
      />

      {/* Unpublish Modal */}
      {showUnpublishModal && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40"
            onClick={() => !isPublishing && setShowUnpublishModal(false)}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl p-6 z-50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Despublicar Agente</h2>
              {!isPublishing && (
                <button
                  onClick={() => setShowUnpublishModal(false)}
                  className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
                >
                  <span className="text-xl">&times;</span>
                </button>
              )}
            </div>

            <p className="text-[#cccccc] mb-6">
              Tem certeza que deseja despublicar o agente "<span className="text-white font-medium">{agent.name}</span>"?
              <br /><br />
              O agente voltará a ser visível apenas no workspace Tributo Devido.
            </p>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowUnpublishModal(false)}
                disabled={isPublishing}
                className="
                  px-4 py-2
                  bg-[#333333] hover:bg-[#444444]
                  text-white text-sm font-medium
                  rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                Cancelar
              </button>
              <button
                onClick={handleUnpublish}
                disabled={isPublishing}
                className="
                  flex items-center gap-2 px-4 py-2
                  bg-purple-600 hover:bg-purple-700
                  text-white text-sm font-medium
                  rounded-lg transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isPublishing ? 'Despublicando...' : 'Confirmar Despublicação'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AgentDetail;
